import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { money } from "./format";
import {
  normalizeDisbursementCoverage,
  coverageServicesLabel,
} from "./coverage";
import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";

const NEEDS_ATTENTION = new Set(["stale", "inconsistent", "unavailable"]);

// Cópia do texto de `DisbursementHeader#STATE_HINT` só pro caso `unavailable` sem
// motivo (o único usado aqui) — importar do outro componente criaria um ciclo entre
// os dois módulos de desembolso, então o texto fixo vive duplicado nos dois lugares.
const PLAIN_UNAVAILABLE_HINT =
  "Esta obra ainda não tem previsto canônico para gerar a curva.";

// `format.js#percentLabel` opera em basis points (o editor de regra grava/lê bps); a
// cobertura Ampli publica uma fração 0..1 (ver `ampliDisbursement.coverage.percentage`),
// então este componente mantém o mesmo cálculo local que `DisbursementHeader` já usa
// pro cabeçalho do módulo, em vez de forçar uma conversão bps só pra reusar a função.
const fractionLabel = (value) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

// `ampliCurveStateLabel` devolve frases inteiras ("Curva inconsistente") pensadas pro
// corpo do popover; o chip só tem espaço pra palavra de estado, capitalizada como um
// rótulo próprio ("Inconsistente"), não como o meio de uma frase.
const chipStateLabel = (status) => {
  const label = ampliCurveStateLabel(status).replace(/^Curva /, "");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const AmpliDisbursementCoverageCard = ({ project }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // `formatNumberToCurrency` lê contexto por dentro — é um hook disfarçado de utilitário
  // (ver MEMORY). Precisa ser chamado em TODO render deste componente, na mesma ordem,
  // então vem aqui em cima: antes do `return null` de obra fora da política Ampli e
  // independente de `coverage` existir — nunca dentro de um `if`/JSX condicional (era
  // isso que `money()` fazia antes, quebrando a ordem de hooks entre fechado/aberto e
  // entre obra com/sem cobertura).
  // `money()` chama o formatador SEMPRE (sem curto-circuito) e sem conversão CUB: as
  // regras são centavos de R$ — mesmo valor que o cabeçalho do módulo mostra.
  const coverage = normalizeDisbursementCoverage(
    project?.ampliDisbursement?.coverage,
  );
  const ruledMoney = money(coverage?.ruledCents);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  // O popover não deve sobreviver a uma troca de obra por baixo do mesmo componente
  // montado (navegação entre projetos sem desmontar o card) — senão o detalhe de uma
  // obra fica aberto mostrando dado da obra seguinte por um render.
  useEffect(() => {
    setOpen(false);
  }, [project?._id]);

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const needsAttention = NEEDS_ATTENTION.has(curve.status);
  const ruledWidth =
    coverage?.percentage != null
      ? Math.min(100, Math.max(0, coverage.percentage * 100))
      : 0;
  // O motivo (quando presente) explica tanto `inconsistent` quanto um `stale` que veio de
  // um `degraded_source` do BE (composição de origem recusada, mas ainda servindo a curva
  // anterior) — mesma regra que `DisbursementHeader` usa para o hint do cabeçalho: motivo,
  // quando existe, prevalece sobre o texto fixo do estado. `unavailable` sem motivo (sem
  // previsto canônico algum) usa o mesmo texto fixo do cabeçalho.
  const stateDetails = {
    stale: "A curva anterior continua no ar enquanto a nova é gerada.",
    unavailable: PLAIN_UNAVAILABLE_HINT,
  };
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : stateDetails[curve.status] ?? null;

  return (
    <div className="ampli-coverage-chip-wrapper" ref={containerRef}>
      <button
        type="button"
        className={`ampli-coverage-chip ampli-coverage-chip--${curve.status}${
          needsAttention ? " ampli-coverage-chip--attention" : ""
        }`}
        aria-label="Cobertura do desembolso Ampli"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ampli-coverage-chip__label">Desembolso Ampli</span>
        <strong>
          {needsAttention
            ? chipStateLabel(curve.status)
            : fractionLabel(coverage?.percentage)}
        </strong>
        <span aria-hidden="true" className="ampli-coverage-chip__caret">
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open ? (
        <section className={`ampli-coverage-card ${curve.status}`}>
          <span className="coverage-eyebrow">Fluxo de caixa · curva Ampli</span>
          <h3>{ampliCurveStateLabel(curve.status)}</h3>
          {detail ? <p>{detail}</p> : null}

          {coverage ? (
            <>
              <div className="ampli-coverage-card__headline">
                <div>
                  <span>Cobertura</span>
                  <strong>{fractionLabel(coverage.percentage)}</strong>
                </div>
                <div>
                  <span>Previsto com regra</span>
                  <strong>{ruledMoney}</strong>
                </div>
              </div>
              {coverage.percentage !== null ? (
                <div
                  className="ampli-coverage-card__rail"
                  role="img"
                  aria-label={`${fractionLabel(
                    coverage.percentage,
                  )} do previsto com regra cadastrada`}
                >
                  <span
                    className="coverage-segment ruled"
                    style={{ width: `${ruledWidth}%` }}
                  />
                  <span
                    className="coverage-segment at-sight"
                    style={{ width: `${100 - ruledWidth}%` }}
                  />
                </div>
              ) : (
                <p>Cobertura percentual indisponível.</p>
              )}
              <p className="ampli-coverage-card__legend">
                {coverageServicesLabel(coverage)}
              </p>
            </>
          ) : null}

          <Link
            className="button button-accent small"
            to={`/dashboard/adiantamentos/${project._id}`}
          >
            Abrir o módulo Desembolso
          </Link>
        </section>
      ) : null}
    </div>
  );
};

export default AmpliDisbursementCoverageCard;
